import React, {
  useCallback,
  useContext, useEffect, useMemo, useState,
} from 'react';
import withErrorBoundary from '@/utils/errorBoundary';
import * as Styles from './FastCheckout.styles';
import { IconArrowLeft } from '@/icons';
import { AlertV2, Button } from 'gatorcomponents';
import { ThemeContext } from 'styled-components';
import HgLogo from './media/logo_hg.svg';
import useLocale from '@/hooks/useLocale';
import { useHistory, useParams } from 'react-router';
import {
  DISCOUNT_TYPES,
  FAST_CHECKOUT_CREDIT_CARD_METHODS, FAST_CHECKOUT_INSTALLMENTS, FAST_CHECKOUT_PAYMENT_METHODS,
} from './FastCheckout.enum';
import { useResize } from '@/hooks/useResize';
import { useCreditCardPayment } from '@/hooks/billing/subscriptions/useCreditCardPayment';
import { CALLER } from '@/hooks/billing/subscriptions/usePaymentModal/usePaymentModal.types';
import { useDispatch, useSelector } from 'react-redux';
import LoaderPage from '../Loader/LoaderPage';
import { FastCheckoutSuccess, FastCheckoutCart } from './pages';
import { loadCreditCard } from '@/redux/actions/creditCard';
import { fastCheckoutActions } from '@/redux/modules';
import { isBRServer } from '@/utils/Validators/validation';

const FastCheckout = () => {
  const theme = useContext(ThemeContext);
  const { fastCheckout: fastCheckoutLocale, routes: routesLocale } = useLocale();
  const currentCreditCard = useSelector(state => state.creditCard);
  const { product: whmcsProductInfo, loading: loadingFastCheckout, purchase } = useSelector(state => state.fastCheckout);
  const params = useParams();
  const { pid } = params;
  const dispatch = useDispatch();
  const { loading: loadingCreditCard } = useSelector(state => state.creditCard);

  const isLoading = loadingCreditCard || loadingFastCheckout;
  const history = useHistory();
  const productNotAdded = !fastCheckoutLocale.products[pid];

  const windowRect = useResize();
  const { width } = windowRect;
  const isMobile = width < 960;

  const { location } = history;
  const { state: routeOriginInfo } = location || {};
  const checkoutDataInfo = routeOriginInfo && routeOriginInfo.checkoutData;
  const {
    coupon, billingCycle, couponAvailable, serviceId,
  } = checkoutDataInfo || {};
  const productContent = useMemo(() => (fastCheckoutLocale.products[pid] ? fastCheckoutLocale.products[pid] : {}), [fastCheckoutLocale.products, pid]);

  const isAUpgradeWithoutServiceId = productContent.isAUpgrade && !serviceId;

  // Note: If product has special rule for discount in addition to the whmcs (like studio) fastcheckout will need to receive a permission to show the discount in route params
  const canShowDiscount = (productContent.productHasSpecialRuleForDiscount && couponAvailable) || !productContent.productHasSpecialRuleForDiscount;

  const allProductInfo = useCallback(() => {
    if (productNotAdded) {
      return {
        productId: '',
        productName: '',
        productCycles: [],
        productDiscount: {},
        productDefaultCycle: '',
        productDefaultPaymentMethod: '',
        productVat: {},
        productIcon: '',
        productTotalValue: 0,
        productIsTrial: false,
        productFinalPrice: 0,
        productBasePrice: 0,
        productPriceWithVat: 0,
        productRenewalDate: '',
        productPaymentMethods: [],
        productInstallments: [],
      };
    }

    const productFinalPrice = () => {
      if (productContent.productHasSpecialRuleForDiscount) {
        if (couponAvailable) return whmcsProductInfo.totalValue; // Note: If the product has a special rule for discount and its avaliable, use the "total value" that is product price + vat + discount

        if (whmcsProductInfo.vat.length > 0) return whmcsProductInfo.priceWithVat; // Note: If the product has a special rule for discount and its unavaliable and have vat, use the "price with vat" that is product price + vat

        return whmcsProductInfo.price; // Note: If the product has a special rule for discount and its unavaliable and dont have vat, use the price that is product base price
      }

      return whmcsProductInfo.totalValue; // Note: For products that dont have special rule for discount use the total value since backend will return the final value
    };

    const baseInfo = {
      productId: whmcsProductInfo.id,
      productName: productContent.name,
      productCycles: whmcsProductInfo.cycles,
      productDiscount: canShowDiscount ? whmcsProductInfo.discount : {},
      productDefaultCycle: productContent.defaultCycle,
      productDefaultPaymentMethod: productContent.defaultPaymentMethod,
      productVat: whmcsProductInfo.vat,
      productIcon: productContent.icon,
      productIsTrial: productContent.isTrial,
      productFinalPrice: productFinalPrice(),
      productBasePrice: whmcsProductInfo.price,
      productPriceWithVat: whmcsProductInfo.priceWithVat,
      productRenewalDate: whmcsProductInfo.renewalDate,
      productPaymentMethods: whmcsProductInfo.paymentMethods,
      productInstallments: whmcsProductInfo.installments,
    };

    return {
      ...baseInfo,
    };
  }, [productNotAdded, whmcsProductInfo, canShowDiscount, productContent, couponAvailable])();

  const byPassConfirmationPayment = useMemo(() => allProductInfo.productIsTrial, [allProductInfo.productIsTrial]);
  const whmcsProductHasContentPaymentMethod = allProductInfo.productPaymentMethods.includes(allProductInfo.productDefaultPaymentMethod);

  // Note: The priority for default billing cycle: The one passed in route params, if didnt passed one, use the one passed in content
  const initialBillingCycle = useMemo(() => (billingCycle || allProductInfo.productDefaultCycle), [billingCycle, allProductInfo.productDefaultCycle]);
  // Note: The priority for default payment methods: The one passed in content if its exists on api array of payment methods, otherwise use the first one in array
  const initialPaymentMethod = useMemo(() => (whmcsProductHasContentPaymentMethod ? allProductInfo.productDefaultPaymentMethod : allProductInfo.productPaymentMethods[0]), [allProductInfo, whmcsProductHasContentPaymentMethod]);

  useEffect(() => {
    dispatch(loadCreditCard());
  }, [dispatch]);

  useEffect(() => {
    if (initialBillingCycle) {
      dispatch(fastCheckoutActions.product.request({
        pid,
        coupon,
        billingCycle: initialBillingCycle,
      }));
    }
  }, [dispatch, pid, routeOriginInfo, allProductInfo.productDefaultCycle, coupon, initialBillingCycle]);

  const handleFinishSuccessPurchase = useCallback(() => {
    const onSuccessInfo = routeOriginInfo && routeOriginInfo.onSuccess;
    const finishRoute = onSuccessInfo && onSuccessInfo.route;
    const routeParams = onSuccessInfo && onSuccessInfo.state;
    history.push(finishRoute || routesLocale.fastcheckout, { ...routeParams });
  }, [history, routesLocale.fastcheckout, routeOriginInfo]);

  useEffect(() => {
    if (productNotAdded || whmcsProductInfo.error || isAUpgradeWithoutServiceId) {
      history.push(routesLocale.home);
      dispatch(fastCheckoutActions.reset());
    }
  }, [pid, fastCheckoutLocale.products, history, productNotAdded, routesLocale, whmcsProductInfo.error, dispatch, isAUpgradeWithoutServiceId]);

  const [isCcButtonEnabled, setCcButtonEnabled] = useState(true);
  const [isCcButtonLoading, setCcButtonLoading] = useState(false);
  const [creditCardMethod, setCreditCardMethod] = useState(FAST_CHECKOUT_CREDIT_CARD_METHODS.NEW);
  const [error, setError] = useState({ error: false, message: '', title: '' });
  const [successPurchase, setSuccessPurchase] = useState(false);
  const [productCycle, setProductCycle] = useState(initialBillingCycle);
  const [paymentMethod, setPaymentMethod] = useState(initialPaymentMethod);
  // eslint-disable-next-line no-unused-vars
  const [installments, setInstallments] = useState(FAST_CHECKOUT_INSTALLMENTS.BRASPAG_CREDIT_1X); // Note: Dont have a way to change installments yet

  useEffect(() => {
    if (purchase.success) {
      setSuccessPurchase(true);
    }
  }, [purchase.success]);

  useEffect(() => {
    if (purchase.error) {
      setError({
        error: true,
        title: fastCheckoutLocale.purchaseErrorToast.title,
        message: fastCheckoutLocale.purchaseErrorToast.description,
      });
    }
  }, [purchase.error, fastCheckoutLocale.purchaseErrorToast]);

  useEffect(() => {
    setPaymentMethod(initialPaymentMethod);
  }, [initialPaymentMethod]);

  useEffect(() => {
    setProductCycle(initialBillingCycle);
  }, [initialBillingCycle]);

  const handleBack = () => {
    history.goBack();
    dispatch(fastCheckoutActions.reset());
  };

  const hasDiscount = allProductInfo.productDiscount.percentage || allProductInfo.productDiscount.value;

  const callFastCheckout = useCallback(() => {
    const discounts = [];
    if (hasDiscount) {
      const discountValue = allProductInfo.productDiscount.type === DISCOUNT_TYPES.PERCENTAGE ? allProductInfo.productDiscount.percentage.split('%').join('') : allProductInfo.productDiscount.value;

      discounts.push({
        value: parseFloat(discountValue),
        type: allProductInfo.productDiscount.type,
        name: coupon,
      });
    }

    dispatch(fastCheckoutActions.purchase.request({
      discount: discounts,
      product: [{
        serviceId: parseFloat(serviceId),
        productId: allProductInfo.productId,
        billingCycle: productCycle,
        domain: '', // FastCheckout dont have domains yet
      }],
      paymentMethod: paymentMethod === FAST_CHECKOUT_PAYMENT_METHODS.CREDIT_CARD && isBRServer ? installments : paymentMethod,
    }));
  }, [allProductInfo, coupon, dispatch, installments, paymentMethod, productCycle, serviceId, hasDiscount]);

  const {
    renderIframe,
    submitCreditCard,
  } = useCreditCardPayment({
    setCcButtonEnabled,
    setCcButtonLoading,
    caller: CALLER.FASTCHECKOUT,
    showIframeMiniCreditCard: false,
    setError,
    callFastCheckout,
  });

  const onFinalizePurchase = () => {
    switch (paymentMethod) {
      case FAST_CHECKOUT_PAYMENT_METHODS.CREDIT_CARD:
        if (creditCardMethod === FAST_CHECKOUT_CREDIT_CARD_METHODS.NEW) {
          return submitCreditCard();
        }
        return callFastCheckout();
      default:
        return callFastCheckout();
    }
  };

  useEffect(() => {
    if (byPassConfirmationPayment && successPurchase) {
      handleFinishSuccessPurchase();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successPurchase]);

  const getLoadingProgress = (...loadings) => {
    if (loadings.length === 0) return 100;

    const loadingsFinished = loadings.filter(v => !v).length;
    return (loadingsFinished / loadings.length) * 100;
  };

  if (isLoading) {
    return (
      <Styles.LoaderWrapper>
        <LoaderPage progress={getLoadingProgress(loadingCreditCard, loadingFastCheckout)} />
      </Styles.LoaderWrapper>
    );
  }

  return (
    <Styles.FastCheckoutWrapper>
      <Styles.Header>
        <Styles.BackButtonWrapper>
          <Button
            onClick={handleBack}
            variant="text"
            label={!isMobile && fastCheckoutLocale.backButton}
            size="large"
            iconLeft={(
              <IconArrowLeft
                size="14"
                color={theme.v2.colors.neutral.lowMedium}
              />
            )}
          />
        </Styles.BackButtonWrapper>
        <Styles.HgLogo alt="HostGator" src={HgLogo} />
      </Styles.Header>
      {successPurchase && !byPassConfirmationPayment && (
        <FastCheckoutSuccess
          handleFinishSuccessPurchase={handleFinishSuccessPurchase}
          locale={fastCheckoutLocale.successPage}
        />
      )}
      {!successPurchase && (
        <FastCheckoutCart
          paymentMethod={paymentMethod}
          onFinalizePurchase={onFinalizePurchase}
          renderIframe={renderIframe}
          productCycle={productCycle}
          currentCreditCard={currentCreditCard}
          isCcButtonEnabled={isCcButtonEnabled}
          isCcButtonLoading={isCcButtonLoading}
          isMobile={isMobile}
          creditCardMethod={creditCardMethod}
          setCreditCardMethod={setCreditCardMethod}
          fastCheckoutLocale={fastCheckoutLocale}
          product={allProductInfo}
          hasDiscount={hasDiscount}
        />
      )}
      {error.error && (
      <Styles.AlertWrapper>
        <AlertV2 onClose={() => setError({ error: false, message: '', title: '' })} withoutBackground={false} width="full" key="errors" variant="critical" title={error.title} description={error.message} />
      </Styles.AlertWrapper>
      )}
    </Styles.FastCheckoutWrapper>
  );
};

export default withErrorBoundary(FastCheckout, 'FastCheckout');
